import type { IconikApiClientInterface } from '@iconik-io/iconik-api/src/client';
import type { EndpointRequest } from '@iconik-io/iconik-api/src/client/request';
import {
  AbortEndpointRequestError,
  type EndpointResponse,
} from '@iconik-io/iconik-api/src/client/response';
import type { RequestOptions } from '@iconik-io/iconik-api/src/types';
import { isCancellation } from '@iconik-io/shared-lib/src/redux/middlewares/http/actions';

import type { AppDispatch } from '../../../store/types';

/**
 * Serves the uploader package from web's redux request path, so its requests
 * get the same auth, token refresh, impersonation headers, error reporting,
 * 401 handling and retries as everything else the app sends. The package's
 * own retry options are not applied on top: the request path already retries
 * what it considers safe, multipart requests included, and retrying around it
 * would multiply the attempts.
 *
 * The store is injected rather than imported: the store imports the reducers
 * that will import the uploader engine.
 */
export const createApiClient = (
  dispatch: AppDispatch,
): IconikApiClientInterface => ({
  request: <Request extends EndpointRequest>(
    request: Request,
    { abortSignal }: RequestOptions<Request> = {},
  ): Promise<EndpointResponse<Request>> =>
    dispatch({ ...request, abortSignal })
      .then(({ status, statusText, headers, body }) => ({
        status,
        statusText,
        headers,
        body,
        request,
      }))
      .catch((error: unknown) => {
        // MultipartUploader.run() returns quietly on this error type, while
        // any other one fails the upload and tears its entities down.
        if (isCancellation(error)) {
          throw new AbortEndpointRequestError();
        }
        throw error;
      }),
});
